import MockAdapter from 'axios-mock-adapter';
import { ActivitySummary, ShareAgreement, SharerAthlete } from 'oas/datasharing';

// Local athlete IDs (recipient side) from the dev account currently used for testing.
// Used as backend `recipient_athlete_id` on agreements where we want the row to
// appear pre-mapped. Exact-name and prior auto-map paths reference these athletes
// by name, not ID, so unused IDs are listed in comments for reference only.
//   Levi Booth     02a50dd7-effd-4bc3-94a7-ef3acf576776
//   Raynard Derricks  0373f938-9478-492a-8daf-853b0f91cf9b
//   Yosh Sheran    02b3d35c-275f-4b4d-92c5-6522905c4a12
const LOCAL_STEVE = '00083565-3cdc-45f5-bc4f-1b53697fe0e1'; // Steve Akbas
const LOCAL_JERRY = '00605cfe-ddbc-492b-bb06-90c449c1ef54'; // Jerry Roberts
const LOCAL_SCOTT = '00cadca5-d35e-460f-9f8f-46c1584cfb8a'; // Scott Anderson

const RECEIVING_LINK_ID = '2b3c4d5e-6f7g-8h9i-0j1k-l2m3n4o5p6q7';

const mockShareAgreements: ShareAgreement[] = [
    //receiving — auto-map test scenarios
    {
        // Backend-mapped: already linked to Steve. Shows as pre-selected & mapped.
        id: 'share-1',
        account_link_id: RECEIVING_LINK_ID,
        recipient_athlete_id: LOCAL_STEVE,
        sharer_athlete_id: 'athlete-s1',
        sharer_athlete_name: 'Jordan Smith',
        sharing_start_date: '2024-09-01T00:00:00Z',
        sharing_end_date: '2024-12-01T00:00:00Z',
        created_at: '2024-12-12T00:00:00Z',
        modified_at: '2024-12-12T00:00:00Z',
        status: 'pending',
        sharer_velocity_max: 11.8,
        sharer_heart_rate_max: 205,
        sharer_mechanical_power_max: 45.678, // shown as 45.68
    },
    {
        // Backend-mapped: already linked to Jerry.
        id: 'share-2',
        account_link_id: RECEIVING_LINK_ID,
        recipient_athlete_id: LOCAL_JERRY,
        sharer_athlete_id: 'athlete-s2',
        sharer_athlete_name: 'Casey Mang',
        sharing_start_date: '2024-09-01T00:00:00Z',
        sharing_end_date: '2024-12-01T00:00:00Z',
        created_at: '2024-12-12T00:00:00Z',
        modified_at: '2024-12-12T00:00:00Z',
        status: 'pending',
        sharer_velocity_max: null,
        sharer_heart_rate_max: 210,
    },
    {
        // PREV-MAPPED path: pending agreement with no backend recipient. The Active
        // agreement `share-prev-mapped` below has the same sharer_athlete_id and
        // recipient = Scott, so this should auto-map to Scott with reason 'prev-mapped'.
        id: 'share-3',
        account_link_id: RECEIVING_LINK_ID,
        recipient_athlete_id: null,
        sharer_athlete_id: 'athlete-s3',
        sharer_athlete_name: "Kurt O'Connor",
        sharing_start_date: '2026-04-01T00:00:00Z',
        sharing_end_date: '2026-06-01T00:00:00Z',
        created_at: '2026-04-01T00:00:00Z',
        modified_at: '2026-04-01T00:00:00Z',
        status: 'pending',
        sharer_velocity_max: 12.3,
        sharer_heart_rate_max: null,
        sharer_mechanical_power_max: 250, // above Cloud's 200 W/kg limit, so never offered
    },
    {
        // EXACT-NAME path: sharer_athlete_name strictly matches local athlete
        // 'Levi Booth' → should auto-map to Levi with reason 'exact-name'.
        id: 'share-4',
        account_link_id: RECEIVING_LINK_ID,
        recipient_athlete_id: null,
        sharer_athlete_id: 'athlete-s4',
        sharer_athlete_name: 'Levi Booth',
        sharing_start_date: '2026-04-01T00:00:00Z',
        sharing_end_date: '2026-06-01T00:00:00Z',
        created_at: '2026-04-01T00:00:00Z',
        modified_at: '2026-04-01T00:00:00Z',
        status: 'pending',
        sharer_velocity_max: null,
        sharer_heart_rate_max: null,
        sharer_mechanical_power_max: 52.5, // its only max, so Review Maxes shows for mechanical power alone
    },
    {
        // EXACT-NAME path: 'Raynard Derricks' matches local athlete by first+last name.
        id: 'share-5',
        account_link_id: RECEIVING_LINK_ID,
        recipient_athlete_id: null,
        sharer_athlete_id: 'athlete-s5',
        sharer_athlete_name: 'Raynard Derricks',
        sharing_start_date: '2026-04-01T00:00:00Z',
        sharing_end_date: '2026-06-01T00:00:00Z',
        created_at: '2026-04-01T00:00:00Z',
        modified_at: '2026-04-01T00:00:00Z',
        status: 'pending',
        sharer_velocity_max: 10.5,
        sharer_heart_rate_max: 198,
        sharer_mechanical_power_max: 38.2,
    },
    {
        // COLLISION (within batch): both share-6 and share-7 name-match the
        // same local athlete. Both are still auto-mapped; the duplicate-row
        // UI surfaces the conflict so the user can resolve it.
        id: 'share-6',
        account_link_id: RECEIVING_LINK_ID,
        recipient_athlete_id: null,
        sharer_athlete_id: 'athlete-s6',
        sharer_athlete_name: 'Yosh Sheran',
        sharing_start_date: '2026-04-01T00:00:00Z',
        sharing_end_date: '2026-06-01T00:00:00Z',
        created_at: '2026-04-01T00:00:00Z',
        modified_at: '2026-04-01T00:00:00Z',
        status: 'pending',
        sharer_velocity_max: 11.2,
        sharer_heart_rate_max: 202,
    },
    {
        id: 'share-7',
        account_link_id: RECEIVING_LINK_ID,
        recipient_athlete_id: null,
        sharer_athlete_id: 'athlete-s7',
        sharer_athlete_name: 'Yosh Sheran',
        sharing_start_date: '2026-04-01T00:00:00Z',
        sharing_end_date: '2026-06-01T00:00:00Z',
        created_at: '2026-04-01T00:00:00Z',
        modified_at: '2026-04-01T00:00:00Z',
        status: 'pending',
        sharer_velocity_max: null,
        sharer_heart_rate_max: 195,
    },
    {
        // BACKEND-TARGET COLLISION: sharer_athlete_name matches Steve, but
        // Steve is already backend-mapped on share-1. Auto-map proposes it
        // anyway; the duplicate-row UI fires on this row + share-1.
        id: 'share-collision-backend',
        account_link_id: RECEIVING_LINK_ID,
        recipient_athlete_id: null,
        sharer_athlete_id: 'athlete-s8',
        sharer_athlete_name: 'Steve Akbas',
        sharing_start_date: '2026-04-01T00:00:00Z',
        sharing_end_date: '2026-06-01T00:00:00Z',
        created_at: '2026-04-01T00:00:00Z',
        modified_at: '2026-04-01T00:00:00Z',
        status: 'pending',
        sharer_velocity_max: 12.0,
        sharer_heart_rate_max: 199,
        sharer_mechanical_power_max: 47,
    },
    {
        // NO MATCH (control): unknown name, no prior — stays unmapped.
        id: 'share-unmapped',
        account_link_id: RECEIVING_LINK_ID,
        recipient_athlete_id: null,
        sharer_athlete_id: 'athlete-s9',
        sharer_athlete_name: 'Stranger Person',
        sharing_start_date: '2026-04-01T00:00:00Z',
        sharing_end_date: '2026-06-01T00:00:00Z',
        created_at: '2026-04-01T00:00:00Z',
        modified_at: '2026-04-01T00:00:00Z',
        status: 'pending',
        sharer_velocity_max: null,
        sharer_heart_rate_max: null,
    },
    {
        // PREV-MAPPED reference (non-Pending). Same sharer_athlete_id as share-3,
        // recipient = Scott → makes share-3 auto-map to Scott.
        id: 'share-prev-mapped',
        account_link_id: RECEIVING_LINK_ID,
        recipient_athlete_id: LOCAL_SCOTT,
        sharer_athlete_id: 'athlete-s3',
        sharer_athlete_name: "Kurt O'Connor",
        sharing_start_date: '2025-09-01T00:00:00Z',
        sharing_end_date: '2025-12-01T00:00:00Z',
        created_at: '2025-08-15T00:00:00Z',
        modified_at: '2026-03-01T00:00:00Z',
        status: 'active',
        sharer_velocity_max: 15.1,
        sharer_heart_rate_max: 201,
    },
    {
        id: 'share-8',
        account_link_id: '4d5e6f7g-8h9i-0j1k-2l3m-n4o5p6q7r8s9',
        recipient_athlete_id: 'athlete-r3',
        sharer_athlete_id: 'athlete-s3',
        sharer_athlete_name: 'Shared Athlete 3',
        sharing_start_date: '2024-12-12T00:00:00Z',
        sharing_end_date: '2025-02-12T00:00:00Z',
        created_at: '2024-11-11T00:00:00Z',
        modified_at: '2024-11-11T00:00:00Z',
        status: 'pending',
        sharer_velocity_max: 12.0,
        sharer_heart_rate_max: 200,
    },
    {
        id: 'share-8-1',
        account_link_id: '4d5e6f7g-8h9i-0j1k-2l3m-n4o5p6q7r8s9',
        recipient_athlete_id: 'athlete-r4',
        sharer_athlete_id: 'athlete-s4',
        sharer_athlete_name: 'Shared Athlete 4',
        sharing_start_date: '2024-12-12T00:00:00Z',
        sharing_end_date: '2025-02-12T00:00:00Z',
        created_at: '2024-11-11T00:00:00Z',
        modified_at: '2024-11-11T00:00:00Z',
        status: 'pending',
        sharer_velocity_max: 12.0,
        sharer_heart_rate_max: 200,
    },
    //sending
    {
        id: 'share-9',
        account_link_id: '1a2b3c4d-5e6f-7g8h-9i0j-k1l2m3n4o5p6',
        recipient_athlete_id: 'athlete-r4',
        sharer_athlete_id: 'athlete-s4',
        sharer_athlete_name: 'Shared Athlete 4',
        sharing_start_date: '2024-07-01T00:00:00Z',
        sharing_end_date: '2025-01-01T00:00:00Z',
        created_at: '2024-01-10T00:00:00Z',
        modified_at: '2024-01-10T00:00:00Z',
        status: 'pending',
    },
    {
        id: 'share-10',
        account_link_id: '1a2b3c4d-5e6f-7g8h-9i0j-k1l2m3n4o5p6',
        recipient_athlete_id: 'athlete-r5',
        sharer_athlete_id: 'athlete-s5',
        sharer_athlete_name: 'Shared Athlete 5',
        sharing_start_date: '2024-07-01T00:00:00Z',
        sharing_end_date: '2025-01-01T00:00:00Z',
        created_at: '2024-01-10T00:00:00Z',
        modified_at: '2024-01-10T00:00:00Z',
        status: 'pending',
    },
];

export function setupShareAgreementsMock(mock: MockAdapter) {
    mock.onGet(`${process.env.REACT_APP_DATASHARING_API_URL}/share-agreements`).reply(() => {
        return [200, mockShareAgreements];
    });

    mock.onPost(new RegExp(`${process.env.REACT_APP_DATASHARING_API_URL}/account-links/.*/share-agreements`)).reply(
        (config) => {
            const urlParts = config.url?.split('/');
            const accountLinkId = urlParts?.[urlParts.length - 2];
            const requestData = JSON.parse(config.data);

            const newShares: ShareAgreement[] = requestData.sharer_athlete_ids.map((athleteId: string) => ({
                id: crypto.randomUUID(),
                account_link_id: accountLinkId,
                recipient_athlete_id: null,
                sharer_athlete_id: athleteId,
                sharer_athlete_name: `Athlete ${athleteId}`,
                sharing_start_date: requestData.sharing_start_date,
                sharing_end_date: requestData.sharing_end_date,
                created_at: new Date().toISOString(),
                modified_at: new Date().toISOString(),
            }));

            mockShareAgreements.push(...newShares);

            return [201, newShares];
        },
    );

    // Bulk approve share agreements
    mock.onPost(`${process.env.REACT_APP_DATASHARING_API_URL}/share-agreements/approvals`).reply((config) => {
        const requestData = JSON.parse(config.data);
        const updatedAgreements: ShareAgreement[] = [];

        requestData.approved?.forEach((item: { share_agreement_id: string; recipient_athlete_id: string }) => {
            const agreement = mockShareAgreements.find((a) => a.id === item.share_agreement_id);
            if (agreement) {
                agreement.status = 'active';
                agreement.recipient_athlete_id = item.recipient_athlete_id;
                agreement.modified_at = new Date().toISOString();
                updatedAgreements.push(agreement);
            }
        });

        requestData.declined?.forEach((item: { share_agreement_id: string }) => {
            const agreement = mockShareAgreements.find((a) => a.id === item.share_agreement_id);
            if (agreement) {
                agreement.status = 'cancelled';
                agreement.modified_at = new Date().toISOString();
                updatedAgreements.push(agreement);
            }
        });

        return [200, updatedAgreements];
    });

    // Get activity summaries for a share agreement
    const mockActivitySummaries: Record<string, ActivitySummary[]> = {
        'share-1': [
            { id: 'act-1a', name: 'Training Session 01/15', start_time: '2024-10-15T09:00:00Z', period_count: 3 },
            { id: 'act-1b', name: 'Match Day 01/20', start_time: '2024-10-20T14:00:00Z', period_count: 2 },
            { id: 'act-1c', name: 'Training Session 01/22', start_time: '2024-10-22T09:00:00Z', period_count: 4 },
        ],
        'share-2': [
            { id: 'act-1a', name: 'Training Session 01/15', start_time: '2024-10-15T09:00:00Z', period_count: 3 },
        ],
        'share-8': [],
    };

    mock.onGet(new RegExp(`${process.env.REACT_APP_DATASHARING_API_URL}/share-agreements/.*/activity-summaries`)).reply(
        (config) => {
            const urlParts = config.url?.split('/');
            const shareAgreementId = urlParts?.[urlParts.length - 2];
            return [200, mockActivitySummaries[shareAgreementId ?? ''] ?? []];
        },
    );
}

const mockSharerAthletes: SharerAthlete[] = [
    // matching ids from agreements
    {
        id: 'athlete-s1',
        first_name: 'Jordan',
        last_name: 'Smith',
        gender: 'male',
        date_of_birth_date: '1992-05-14',
        height: 185.5,
        weight: 82.0,
        jersey: '10',
        heart_rate_max: 195.0,
        velocity_max: 12.4,
        acceleration_max: 4.2,
        player_load_max: 10.0,
    },
    {
        id: 'athlete-s2',
        first_name: 'Casey',
        last_name: 'Mang',
        gender: 'female',
        date_of_birth_date: '1995-11-22',
        height: 170.0,
        weight: 64.5,
        jersey: '07',
        heart_rate_max: 190.0,
        velocity_max: 18.8,
        acceleration_max: 3.9,
        player_load_max: 10.0,
    },
    {
        id: 'athlete-s3',
        first_name: 'Kurt',
        last_name: "O'Connor",
        gender: 'unspecified',
        date_of_birth_date: '1998-02-03',
        height: 178.2,
        weight: 75.1,
        jersey: '22',
        heart_rate_max: 201.0,
        velocity_max: 15.1,
        acceleration_max: 4.5,
        player_load_max: 10.0,
    },
    {
        id: 'athlete-s4',
        first_name: 'Alex',
        last_name: 'Vidales',
        gender: 'male',
        date_of_birth_date: '1990-08-30',
        height: 192.0,
        weight: 95.3,
        jersey: '99',
        heart_rate_max: 188.0,
        velocity_max: 16.2,
        acceleration_max: 3.5,
        player_load_max: 10.0,
    },
    {
        id: 'athlete-s5',
        first_name: 'Samantha',
        last_name: 'Puckett',
        gender: 'female',
        date_of_birth_date: '1996-12-12',
        height: 165.4,
        weight: 58.9,
        jersey: '03',
        heart_rate_max: 198.0,
        velocity_max: 19.5,
        acceleration_max: 4.1,
        player_load_max: 10.0,
    },
    // athlete-s6 and athlete-s7 are duplicate-by-design: their share agreements both
    // name "Yosh Sheran" to exercise the in-batch auto-map collision path.
    {
        id: 'athlete-s6',
        first_name: 'Yosh',
        last_name: 'Sheran',
        gender: 'male',
        date_of_birth_date: '1999-05-10',
        height: 180.0,
        weight: 79.0,
        jersey: '11',
        heart_rate_max: 200.0,
        velocity_max: 13.8,
        acceleration_max: 4.2,
        player_load_max: 10.0,
    },
    {
        id: 'athlete-s7',
        first_name: 'Yoshua',
        last_name: 'Sheran',
        gender: 'male',
        date_of_birth_date: '', // testing fallback to epoch date
        height: 172.0,
        weight: 66.0,
        jersey: '10',
        heart_rate_max: 182.0,
        velocity_max: 13.5,
        acceleration_max: 4.8,
        player_load_max: 10.0,
    },
    {
        id: 'athlete-s8',
        first_name: 'Steven',
        last_name: 'Akbas',
        gender: 'male',
        date_of_birth_date: '1985-04-22',
        height: 182.0,
        weight: 81.0,
        jersey: '21',
        heart_rate_max: 196.0,
        velocity_max: 14.2,
        acceleration_max: 4.0,
        player_load_max: 10.0,
    },
    {
        id: 'athlete-s9',
        first_name: 'Stranger',
        last_name: 'Person',
        gender: 'unspecified',
        date_of_birth_date: '1993-01-01',
        height: 170.0,
        weight: 70.0,
        jersey: '00',
        heart_rate_max: 190.0,
        velocity_max: 12.0,
        acceleration_max: 3.5,
        player_load_max: 10.0,
    },
];

export function setupSharerAthleteMock(mock: MockAdapter) {
    // capture the shareAgreementId
    const urlPattern = new RegExp(`${process.env.REACT_APP_DATASHARING_API_URL}/share-agreements/(.*)/sharer-athlete`);

    mock.onGet(urlPattern).reply((config) => {
        const match = config.url?.match(urlPattern);
        const agreementId = match?.[1];

        if (!agreementId) {
            return [400, { message: 'No agreement ID provided' }];
        }

        // find the agreement to get the sharer_athlete_id, then find the athlete
        const agreement = mockShareAgreements.find((a) => a.id === agreementId);
        const athlete = mockSharerAthletes.find((a) => a.id === agreement?.sharer_athlete_id);

        if (athlete) {
            return [200, athlete];
        }

        return [404, { message: 'Athlete not found' }];
    });
}
